import { APP_INITIALIZER, enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { environment } from '@environments/environment';
import { AppComponent } from '@app/app.component';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AppRoutes } from '@app/app.routes';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { PublicClientApplication } from '@azure/msal-browser';
import { MSAL_INSTANCE, MsalService } from '@azure/msal-angular';
import { ConfigService } from '@shared/service/app.config.service';
import { MaintenanceService } from '@shared/service/maintenance.service';
import { GroupInterceptor } from '@shared/interceptors/group.interceptor';
import { AuthInterceptor } from '@shared/interceptors/auth.interceptor';
import { MaintenanceInterceptor } from '@shared/interceptors/maintenance.interceptor';
import { NotificationsService } from '@shared/service/notifications.service';
import { FormConfigService } from '@shared/configurable/form-config.service';
import { TableConfigService } from '@shared/configurable/table-config.service';
import { UserService } from '@shared/service/user.service';
import { LocaleService } from '@shared/service/locale.service';
import { PermissionService } from '@shared/service/permission.service';
import { GroupsService } from '@shared/service/groups.service';
import { OptionsService } from '@private/services/options.service';
import { ConfirmationService } from '@ui/services/confirmationservice';
import { DynamicDialogModule } from '@ui/components/dynamicdialog/dynamicdialog';
import { DynamicDialogConfig } from '@ui/components/dynamicdialog/dynamicdialog-config';
import { DialogService } from '@ui/components/dynamicdialog/dialogservice';
import { DynamicDialogRef } from '@ui/components/dynamicdialog/dynamicdialog-ref';
import { MessageService } from '@ui/services/messageservice';

if (environment.production) {
  enableProdMode();
}
export const msalInstance = new PublicClientApplication({
  auth: {
    clientId: environment.msSSO.clientId,
    authority: environment.msSSO.authority,
    redirectUri: `${window.location.origin}/${environment.msSSO.callbackUri}`,
  },
});

export function configProviderFactory(provider: ConfigService) {
  return () => provider.loadTenant();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(HttpClient),
    importProvidersFrom(TranslateModule.forRoot()),
    importProvidersFrom(DynamicDialogModule),
    provideRouter(
      AppRoutes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      })
    ),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    {
      provide: APP_INITIALIZER,
      useFactory: configProviderFactory,
      deps: [ConfigService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (maintenance: MaintenanceService) => () => maintenance.init(),
      deps: [MaintenanceService],
      multi: true,
    },
    { provide: MSAL_INSTANCE, useValue: msalInstance },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => msalInstance.initialize(),
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: GroupInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: MaintenanceInterceptor, multi: true },
    MessageService,
    UserService,
    LocaleService,
    OptionsService,
    PermissionService,
    GroupsService,
    NotificationsService,
    DynamicDialogConfig,
    FormConfigService,
    TableConfigService,
    MsalService,
    DialogService,
    DynamicDialogRef,
    ConfirmationService,
  ],
}).catch(err => console.error(err));
